/* eslint-disable simple-import-sort/imports */
/* eslint-disable prettier/prettier */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-no-constructed-context-values */
/* eslint-disable react/function-component-definition */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserType } from '../types/user';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, userData: User) => void;
  logout: () => void;
}

// OAuth configuration
export const OAUTH_CONFIG = {
  google: {
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
    redirectUri: process.env.REACT_APP_GOOGLE_REDIRECT_URI || '',
  },
  apple: {
    clientId: process.env.REACT_APP_APPLE_CLIENT_ID || '',
    redirectUri: process.env.REACT_APP_APPLE_REDIRECT_URI || '',
  },
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on initial load from localStorage
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');

      if (!token || !storedUser) {
        setIsLoading(false);
        return;
      }

      try {
        // Parse the stored user data
        const userData = JSON.parse(storedUser);
        if (userData) {
          // Ensure name is populated when restoring from localStorage
          if (!userData.name || userData.name.trim() === '') {
            const firstLast = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
            userData.name = firstLast || userData.email?.split('@')[0] || 'User';
            // Persist the fixed name back to localStorage
            localStorage.setItem('user', JSON.stringify(userData));
          }
          setUser(userData);
        } else {
          // If no valid user data, clear the token
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
        }
      } catch (error) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Listen for storage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      // Handle login in another tab
      if (e.key === 'access_token' && e.newValue && !e.oldValue) {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            setUser(userData);
            // Reload the page to sync state
            window.location.reload();
          } catch (error) {
            // Silent fail
          }
        }
      }

      // Handle logout in another tab
      if (e.key === 'access_token' && !e.newValue && e.oldValue) {
        setUser(null);
        window.location.href = '/login';
      }

      // Handle user change (different account login)
      if (e.key === 'user' && e.newValue && e.oldValue) {
        try {
          const newUser = JSON.parse(e.newValue);
          const oldUser = JSON.parse(e.oldValue);
          
          // If user ID changed, it's a different account
          if (newUser.id !== oldUser.id) {
            setUser(newUser);
            // Reload to sync with new user session
            window.location.reload();
          }
        } catch (error) {
          // Silent fail
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const login = (token: string, userData: User) => {
    // Ensure name is always populated with a meaningful value
    const userToStore = { ...userData };
    if (!userToStore.name || userToStore.name.trim() === '') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const extra = userData as Record<string, any>;
      const firstLast = `${extra.firstName || ''} ${extra.lastName || ''}`.trim();
      userToStore.name = firstLast || userToStore.email?.split('@')[0] || 'User';
    }

    // eslint-disable-next-line no-console
    console.log('[AuthContext] login called with userData:', { name: userToStore.name, email: userToStore.email });

    localStorage.setItem('access_token', token);
    localStorage.setItem('user', JSON.stringify(userToStore));

    // Clear any pending OTP resend cooldown — a successful login/registration
    // means the previous OTP session is done and shouldn't block a future one
    sessionStorage.removeItem('otp_resend_cooldown_end');

    // Store roleId if available for permission system
    if (userData.roleId) {
      localStorage.setItem('roleId', userData.roleId.toString());
    }

    // Store clubId if available
    if (userData.clubId) {
      localStorage.setItem('clubId', userData.clubId.toString());
    }

    // Store userRole for backward compatibility
    if (userData.userType) {
      localStorage.setItem('userRole', userData.userType);
    }

    setUser(userToStore);
  };

  const logout = () => {
    // Clear all auth data from localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedRole');
    localStorage.removeItem('SocialLoginToken');
    localStorage.removeItem('roleId');
    localStorage.removeItem('clubId');
    localStorage.removeItem('userRole');
    // Clear OTP resend cooldown so it starts fresh on next login
    sessionStorage.removeItem('otp_resend_cooldown_end');
    // Clear the user state
    setUser(null);
    // Hard reload to login page to ensure all component state (including OTP timers) is reset
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
